/**
 * Flood-fill canvas ImageData at (x,y) with RGBA fill color.
 * Tolerance + expand pass remove anti-aliased white fringe next to strokes
 * (stars/circles) so fill meets the outline edge-to-edge.
 */
export function floodFill(
  imageData,
  startX,
  startY,
  fillRgba,
  tolerance = 160,
  expandPasses = 2
) {
  const { width, height, data } = imageData;
  const x0 = Math.floor(startX);
  const y0 = Math.floor(startY);
  if (x0 < 0 || y0 < 0 || x0 >= width || y0 >= height) return;

  const idx0 = (y0 * width + x0) * 4;
  const tr = data[idx0];
  const tg = data[idx0 + 1];
  const tb = data[idx0 + 2];
  const ta = data[idx0 + 3];
  const [fr, fg, fb, fa] = fillRgba;

  if (colorDist(tr, tg, tb, ta, fr, fg, fb, fa) === 0) return;

  const matchStart = (i) =>
    colorDist(data[i], data[i + 1], data[i + 2], data[i + 3], tr, tg, tb, ta) <= tolerance;

  const isFill = (i) =>
    data[i] === fr && data[i + 1] === fg && data[i + 2] === fb && data[i + 3] === fa;

  /** Fringe: still close to start OR mostly light / low-chroma (AA halo), not a solid outline. */
  const isFringe = (i) => {
    if (isFill(i)) return false;
    const r = data[i];
    const g = data[i + 1];
    const b = data[i + 2];
    const a = data[i + 3];
    if (colorDist(r, g, b, a, tr, tg, tb, ta) <= tolerance + 40) return true;
    const maxc = Math.max(r, g, b);
    const minc = Math.min(r, g, b);
    const chroma = maxc - minc;
    const luminance = 0.299 * r + 0.587 * g + 0.114 * b;
    // Soft blended edge (not a vivid solid stroke)
    return luminance >= 140 && chroma <= 90;
  };

  const filled = [];
  const stack = [[x0, y0]];
  const seen = new Uint8Array(width * height);

  while (stack.length) {
    const [x, y] = stack.pop();
    if (x < 0 || y < 0 || x >= width || y >= height) continue;
    const p = y * width + x;
    if (seen[p]) continue;
    const i = p * 4;
    if (!matchStart(i)) continue;
    seen[p] = 1;
    data[i] = fr;
    data[i + 1] = fg;
    data[i + 2] = fb;
    data[i + 3] = fa;
    filled.push(p);
    stack.push([x + 1, y], [x - 1, y], [x, y + 1], [x, y - 1]);
  }

  // Grow fill into anti-aliased halo so it butts up against the real stroke
  const neighbors = [
    [1, 0],
    [-1, 0],
    [0, 1],
    [0, -1],
    [1, 1],
    [1, -1],
    [-1, 1],
    [-1, -1],
  ];

  let frontier = filled;
  for (let pass = 0; pass < expandPasses; pass++) {
    const next = [];
    for (const p of frontier) {
      const x = p % width;
      const y = (p / width) | 0;
      for (const [dx, dy] of neighbors) {
        const nx = x + dx;
        const ny = y + dy;
        if (nx < 0 || ny < 0 || nx >= width || ny >= height) continue;
        const np = ny * width + nx;
        if (seen[np]) continue;
        const ni = np * 4;
        if (!isFringe(ni)) continue;
        seen[np] = 1;
        data[ni] = fr;
        data[ni + 1] = fg;
        data[ni + 2] = fb;
        data[ni + 3] = fa;
        next.push(np);
      }
    }
    if (!next.length) break;
    frontier = next;
  }
}

function colorDist(r1, g1, b1, a1, r2, g2, b2, a2) {
  return Math.abs(r1 - r2) + Math.abs(g1 - g2) + Math.abs(b1 - b2) + Math.abs(a1 - a2);
}

export function cssColorToRgba(color) {
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = 1;
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = color;
  ctx.fillRect(0, 0, 1, 1);
  const [r, g, b, a] = ctx.getImageData(0, 0, 1, 1).data;
  return [r, g, b, a];
}
